import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiCall } from '../../shared/hooks/useApiCall';
import { useToast } from '../../shared/contexts/ToastContext';
import { useCompany } from '../../company/contexts/CompanyContext';
import { User } from '../types/User';
import { Document } from '../types/Document';
import { Company } from '../../company/types/Company';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { apiCall } = useApiCall();
  const { error: showError, success: showSuccess } = useToast();
  const { companies, isLoading: isLoadingCompanies, refreshCompanies } = useCompany();
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'users' | 'companies' | 'documents'>('users');
  
  // Translation settings state
  const [showTranslationSettings, setShowTranslationSettings] = useState(false);
  const [selectedQualityModel, setSelectedQualityModel] = useState<'gpt-4o-mini' | 'gpt-5' | 'gpt-5-mini' | 'gpt-5.2'>('gpt-4o-mini');
  const [isSubmittingModel, setIsSubmittingModel] = useState(false);
  const [isLoadingCurrentModel, setIsLoadingCurrentModel] = useState(false);

  // OpenAI bakiye kontrol state
  const [openaiStatus, setOpenaiStatus] = useState<number | null>(null);
  const [isCheckingOpenai, setIsCheckingOpenai] = useState(false);
  
  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Documents state
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [selectedCompanyForDocs, setSelectedCompanyForDocs] = useState<Company | null>(null);
  
  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createStep, setCreateStep] = useState<'email' | 'create' | 'assign-role' | 'success'>('email');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignConfirmModalOpen, setIsAssignConfirmModalOpen] = useState(false);
  const [isAssignDocConfirmModalOpen, setIsAssignDocConfirmModalOpen] = useState(false);
  const [isRemoveAssignModalOpen, setIsRemoveAssignModalOpen] = useState(false);
  const [isRemoveDocAssignModalOpen, setIsRemoveDocAssignModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ userId: number; username: string } | null>(null);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [assignmentData, setAssignmentData] = useState<{ companyId: number; companyName: string; userId: number; username: string } | null>(null);
  const [docAssignmentData, setDocAssignmentData] = useState<{ documentId: string; documentName: string; userId: number; username: string } | null>(null);
  const [removeAssignmentData, setRemoveAssignmentData] = useState<{ companyId: number; companyName: string; userId: number; username: string } | null>(null);
  const [removeDocAssignmentData, setRemoveDocAssignmentData] = useState<{ documentId: string; documentName: string; userId: number; username: string } | null>(null);
  const [editedUsername, setEditedUsername] = useState('');
  const [editedRole, setEditedRole] = useState<'admin' | 'editor' | 'redactor'>('editor');
  
  // Create user state
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'editor' | 'redactor'>('editor');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [existingUserId, setExistingUserId] = useState<number | null>(null);
  const [existAppName, setExistAppName] = useState<string | null>(null);
  const [createdUserEmail, setCreatedUserEmail] = useState('');
  const [createdUserPassword, setCreatedUserPassword] = useState('');
  
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    role: 'editor' as 'admin' | 'editor' | 'redactor',
    password: ''
  });

  // Kullanıcıları getir
  const fetchUsers = async () => {
    setIsLoading(true);
    
    const result = await apiCall<User[]>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/get_users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Tüm kullanıcılar:', data);
      // Sadece translate app'ine ait kullanıcıları filtrele
      const translateUsers = data.filter((user: User) => user.app_name === 'translate');
      console.log('Translate kullanıcıları:', translateUsers);
      return translateUsers;
    }, {
      loadingKey: 'fetch-users',
      loadingMessage: 'Kullanıcılar yükleniyor...',
      errorMessage: 'Kullanıcılar yüklenirken hata oluştu',
      showToast: false
    });

    if (result) {
      setUsers(result);
    }
    
    setIsLoading(false);
  };

  // Component yüklendiğinde kullanıcıları getir
  useEffect(() => {
    fetchUsers();
  }, []);

  // 6 haneli rastgele şifre üretimi
  const generatePassword = (length: number = 6): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Panoya kopyalama fonksiyonu
  const copyToClipboard = (text: string, label: string) => {
    // Fallback kopyalama metodu (eski tarayıcılar ve HTTP için)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        showSuccess('Başarılı', `${label} panoya kopyalandı`);
      }).catch(() => {
        fallbackCopyToClipboard(text, label);
      });
    } else {
      fallbackCopyToClipboard(text, label);
    }
  };

  // Fallback kopyalama fonksiyonu
  const fallbackCopyToClipboard = (text: string, label: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      showSuccess('Başarılı', `${label} panoya kopyalandı`);
    } catch (err) {
      showError('Hata', 'Kopyalama başarısız oldu');
    }
    document.body.removeChild(textArea);
  };

  // Translation Settings - Get current selected model
  const getCurrentSelectedModel = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user?.id) {
      showError('Kullanıcı bilgisi bulunamadı');
      return;
    }

    setIsLoadingCurrentModel(true);
    
    const result = await apiCall(async () => {
      console.log('get_selected_batch_model endpoint\'e gönderiliyor:', { user_id: user.id });

      const response = await fetch('https://n8n.mahir.ai/webhook/get_selected_batch_model', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('get_selected_batch_model yanıtı:', data);
      
      return data;
    }, {
      loadingKey: 'get-current-model',
      loadingMessage: 'Mevcut model bilgisi alınıyor...',
      showToast: false
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].selected_batch_model) {
      // Gelen model adını kontrol et ve geçerli bir model ise set et
      const modelName = result[0].selected_batch_model;
      if (modelName === 'gpt-4o-mini' || modelName === 'gpt-5' || modelName === 'gpt-5-mini' || modelName === 'gpt-5.2') {
        setSelectedQualityModel(modelName);
      }
    }
    
    setIsLoadingCurrentModel(false);
  };

  // Translation Settings - Handle model selection
  const handleModelSelection = async (modelName: 'gpt-4o-mini' | 'gpt-5' | 'gpt-5-mini' | 'gpt-5.2') => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user?.id) {
      showError('Kullanıcı bilgisi bulunamadı');
      return;
    }

    setIsSubmittingModel(true);
    
    const result = await apiCall(async () => {
      const currentTimestamp = Math.floor(Date.now() / 1000); // Epoch timestamp in seconds
      
      console.log('select_batch_model endpoint\'e gönderiliyor:', { 
        user_id: user.id, 
        model: modelName, 
        timestamp: currentTimestamp 
      });

      const response = await fetch('https://n8n.mahir.ai/webhook/select_batch_model', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          model: modelName,
          timestamp: currentTimestamp
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('select_batch_model yanıtı:', data);
      
      return data;
    }, {
      loadingKey: 'model-selection',
      loadingMessage: 'Model güncelleniyor...',
      showToast: false // Manuel toast göstereceğiz
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      setSelectedQualityModel(modelName);
      showSuccess(`Model başarıyla ${modelName} olarak güncellendi`);
      setShowTranslationSettings(false);
    } else {
      showError('Model güncellenirken bir hata oluştu');
    }
    
    setIsSubmittingModel(false);
  };

  // Modal açma - başlangıç state'ini sıfırla
  const handleOpenCreateModal = () => {
    setCreateStep('email');
    setNewUserEmail('');
    setNewUserRole('editor');
    setGeneratedPassword('');
    setExistingUserId(null);
    setExistAppName(null);
    setCreatedUserEmail('');
    setCreatedUserPassword('');
    setIsCreateModalOpen(true);
  };

  // Email kontrolü
  const handleCheckEmail = async () => {
    if (!newUserEmail.trim()) {
      showError('Hata', 'E-posta adresi zorunludur');
      return;
    }

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/check_user_exists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newUserEmail
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    }, {
      loadingKey: 'check-email',
      loadingMessage: 'Kontrol ediliyor...',
      errorMessage: 'E-posta kontrolü başarısız oldu',
      showToast: false
    });

    if (result) {
      console.log('=== Translate Panel - Email Check Start ===');
      console.log('Raw result:', JSON.stringify(result, null, 2));
      console.log('Is Array:', Array.isArray(result));
      
      // Array formatında dönebilir (kullanıcı birden fazla uygulamada kayıtlı)
      if (Array.isArray(result) && result.length > 0) {
        // Yeni format: [{ "app_names": "fr_update, translate" }]
        const firstItem = result[0];
        console.log('First item:', firstItem);
        console.log('Has app_names?', !!firstItem.app_names);
        console.log('Has app_name?', !!firstItem.app_name);
        
        let appNames: string[] = [];
        
        if (firstItem.app_names) {
          // Yeni format: comma-separated string
          console.log('app_names (raw):', firstItem.app_names);
          console.log('app_names type:', typeof firstItem.app_names);
          appNames = String(firstItem.app_names)
            .split(',')
            .map((name: string) => name.replace(/\s+/g, ''))
            .filter(Boolean);
          console.log('After split and clean:', appNames);
        } else if (firstItem.app_name) {
          // Eski format fallback: tek app_name
          console.log('Using old format app_name:', firstItem.app_name);
          appNames = [String(firstItem.app_name).replace(/\s+/g, '')];
        }
        
        console.log('Final cleaned app names:', appNames);
        console.log('Checking if includes "translate":', appNames.includes('translate'));
        console.log('Each app name check:');
        appNames.forEach((app, i) => {
          console.log(`  [${i}]: "${app}" === "translate"?`, app === 'translate');
        });
        
        if (appNames.includes('translate')) {
          // Bu uygulamada zaten var - Hiçbir işlem yapma
          console.log('User already exists in translate, showing error');
          setIsCreateModalOpen(false);
          showError('Hata', 'Bu kullanıcı zaten Çevirmen uygulamasında tanımlı');
        } else {
          console.log('User does not exist in translate, proceeding to role assignment');
          // Başka uygulamalarda var ama bu uygulamada yok - rol ataması yap
          // user_id'yi email'den bulmak için users listesinde ara
          const userWithEmail = users.find(u => u.email === newUserEmail);
          if (userWithEmail) {
            setExistingUserId(userWithEmail.user_id);
          } else {
            // Eğer users listesinde yoksa, tüm kullanıcıları çekip ara
            const allUsersResult = await apiCall<User[]>(async () => {
              const response = await fetch('https://n8n.mahir.ai/webhook/get_users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
              });
              if (!response.ok) throw new Error('Failed to fetch users');
              return await response.json();
            }, { showToast: false });
            
            if (allUsersResult) {
              const foundUser = allUsersResult.find((u: User) => u.email === newUserEmail);
              if (foundUser) {
                setExistingUserId(foundUser.user_id);
              }
            }
          }
          
          // Sadece diğer uygulamaları göster (translate hariç)
          const otherAppNames = appNames.filter(app => app !== 'translate');
          console.log('Other app names (without translate):', otherAppNames);
          const appDisplayNames = otherAppNames.map((app: string) => 
            app === 'translate' ? 'Çevirmen' : app === 'fr_update' ? 'İçerik Güncelleme' : app
          ).join(' ve ');
          console.log('Display names for modal:', appDisplayNames);
          setExistAppName(appDisplayNames);
          setCreateStep('assign-role');
        }
        console.log('=== End Email Check ===');
      } else {
        // Object format: { user_id: number | null, exist_app_name: string | null }
        console.log('Using object format');
        console.log('user_id:', result.user_id);
        console.log('exist_app_name:', result.exist_app_name);
        
        if (result.user_id === null || result.exist_app_name === null) {
          // Yeni kullanıcı - yaratma formuna geç
          console.log('New user - going to create form');
          setGeneratedPassword(generatePassword(6));
          setCreateStep('create');
        } else {
          // Mevcut kullanıcı - exist_app_name comma-separated olabilir
          const existAppNameStr = String(result.exist_app_name);
          console.log('Existing user, processing exist_app_name:', existAppNameStr);
          
          // Comma-separated ise split et
          const appNames = existAppNameStr
            .split(',')
            .map((name: string) => name.replace(/\s+/g, ''))
            .filter(Boolean);
          
          console.log('Parsed app names from exist_app_name:', appNames);
          console.log('Checking if includes "translate":', appNames.includes('translate'));
          
          setExistingUserId(result.user_id);
          
          if (appNames.includes('translate')) {
            // Aynı uygulamada zaten var
            console.log('User already exists in translate, showing error');
            setIsCreateModalOpen(false);
            showError('Hata', 'Bu kullanıcı zaten Çevirmen uygulamasında tanımlı');
          } else {
            // Farklı uygulamada var - rol ataması yap
            console.log('User does not exist in translate, proceeding to role assignment');
            
            // Sadece diğer uygulamaları göster (translate hariç)
            const otherAppNames = appNames.filter(app => app !== 'translate');
            const appDisplayNames = otherAppNames.map((app: string) => 
              app === 'translate' ? 'Çevirmen' : app === 'fr_update' ? 'İçerik Güncelleme' : app
            ).join(' ve ');
            console.log('Display names for modal:', appDisplayNames);
            setExistAppName(appDisplayNames);
            setCreateStep('assign-role');
          }
        }
        console.log('=== End Email Check ===');
      }
    }
  };

  // Şirketler yüklendiğinde company context'i kullan
  useEffect(() => {
    if (!isLoadingCompanies) {
      refreshCompanies();
    }
  }, []);

  // Belirli bir şirketin dökümanlarını getir
  const fetchDocuments = async (companyId: number) => {
    setIsLoadingDocuments(true);
    
    const result = await apiCall<Document[]>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/get_documents', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_id: companyId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Dökümanlar:', data);
      
      // Boş obje kontrolü - [{}] formatında gelirse kayıt yok demektir
      if (Array.isArray(data) && data.length === 1 && Object.keys(data[0]).length === 0) {
        return [];
      }
      
      return data;
    }, {
      loadingKey: 'fetch-documents',
      loadingMessage: 'Dökümanlar yükleniyor...',
      errorMessage: 'Dökümanlar yüklenirken hata oluştu',
      showToast: false
    });

    if (result) {
      setDocuments(result);
    } else {
      setDocuments([]);
    }
    
    setIsLoadingDocuments(false);
  };

  // Tarih formatlama fonksiyonu
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Rol rengini belirle
  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
      case 'yönetici':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'editor':
      case 'editör':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'redactor':
      case 'redaktör':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Rol etiketini Türkçe'ye çevir
  const getRoleLabel = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'Yönetici';
      case 'editor':
        return 'Editör';
      case 'redactor':
        return 'Redaktör';
      default:
        return role;
    }
  };

  // Uygulama ismini Türkçe'ye çevir
  const getAppDisplayName = (appName: string) => {
    const names: { [key: string]: string } = {
      'translate': 'Çevirmen',
      'fr_update': 'İçerik Güncelleme'
    };
    // Eğer birden fazla uygulama varsa (örn: "translate ve fr_update")
    if (appName.includes(' ve ') || appName.includes(',')) {
      return appName.split(/\s+ve\s+|,\s*/).map(app => names[app.trim()] || app).join(' ve ');
    }
    return names[appName] || appName;
  };

  // Eski rol değerlerini yeni değerlere dönüştür
  const mapLegacyRole = (role?: string): 'admin' | 'editor' | 'redactor' => {
    if (!role) return 'editor'; // default if undefined
    const roleLower = role.toLowerCase();
    if (roleLower === 'translator' || roleLower === 'çevirmen') return 'editor';
    if (roleLower === 'controller' || roleLower === 'kontrolcü' || roleLower === 'kontrolcu') return 'redactor';
    if (roleLower === 'admin' || roleLower === 'yönetici') return 'admin';
    return 'editor'; // default
  };

  // Kullanıcı adı değiştiğinde şifreyi otomatik oluştur
  const handleUsernameChange = (username: string) => {
    setNewUser(prev => ({
      ...prev,
      username,
      password: username ? `${username}123++` : ''
    }));
  };

  // Yeni kullanıcı oluştur
  const handleCreateUser = async () => {
    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/create_user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: newUserEmail,
          email: newUserEmail,
          role: newUserRole,
          password: generatedPassword,
          app_name: 'translate'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    }, {
      loadingKey: 'create-user',
      loadingMessage: 'Kullanıcı oluşturuluyor...',
      errorMessage: 'Kullanıcı oluşturulurken hata oluştu',
      showToast: false
    });

    console.log('Create user response:', result);
    console.log('Is array?', Array.isArray(result));
    console.log('Length:', result?.length);
    console.log('First item:', result?.[0]);
    console.log('Success value:', result?.[0]?.success);
    console.log('Success type:', typeof result?.[0]?.success);
    console.log('Success strict check:', result?.[0]?.success === true);
    console.log('Success loose check:', result?.[0]?.success == true);

    // Response hem array hem object olarak dönebilir
    const isSuccess = Array.isArray(result) 
      ? (result.length > 0 && result[0]?.success)
      : result?.success;

    if (result && isSuccess) {
      setCreatedUserEmail(newUserEmail);
      setCreatedUserPassword(generatedPassword);
      setCreateStep('success');
      await fetchUsers();
    } else {
      showError('Hata', 'Kullanıcı oluşturulamadı');
    }
  };

  // Mevcut kullanıcıya rol atama
  const handleAssignRole = async () => {
    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/insert_user_app_roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: existingUserId,
          app_name: 'translate',
          role: newUserRole
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    }, {
      loadingKey: 'assign-role',
      loadingMessage: 'Rol atanıyor...',
      errorMessage: 'Rol ataması başarısız oldu',
      showToast: false
    });

    console.log('Assign role response:', result);

    // Response hem array hem object olarak dönebilir
    const isSuccess = Array.isArray(result) 
      ? (result.length > 0 && result[0]?.success)
      : result?.success;

    if (result && isSuccess) {
      showSuccess('Başarılı', 'Rol ataması başarıyla yapıldı');
      setIsCreateModalOpen(false);
      await fetchUsers();
    } else {
      showError('Hata', 'Rol ataması başarısız oldu');
    }
  };

  // Düzenleme modalını aç
  const handleOpenEditModal = (user: User) => {
    setUserToEdit(user);
    setEditedUsername(user.username);
    setEditedRole(mapLegacyRole(user.app_role));
    setIsEditModalOpen(true);
  };

  // Kullanıcıyı düzenle
  const handleEditUser = async () => {
    if (!userToEdit) return;

    const usernameChanged = editedUsername !== userToEdit.username;
    const roleChanged = editedRole !== userToEdit.app_role;

    if (!usernameChanged && !roleChanged) {
      showError('Hata', 'Hiçbir değişiklik yapılmadı');
      return;
    }

    let success = true;

    // Kullanıcı adı değiştiyse
    if (usernameChanged) {
      if (!editedUsername.trim()) {
        showError('Hata', 'Kullanıcı adı boş olamaz');
        return;
      }

      const result = await apiCall<any>(async () => {
        const response = await fetch('https://n8n.mahir.ai/webhook/change_username', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userToEdit.user_id,
            username: editedUsername
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Kullanıcı adı değiştirme yanıtı:', data);
        return data;
      }, {
        loadingKey: 'change-username',
        loadingMessage: 'Kullanıcı adı değiştiriliyor...',
        errorMessage: 'Kullanıcı adı değiştirilirken hata oluştu',
        showToast: false
      });

      if (!result || !Array.isArray(result) || result.length === 0 || result[0].success !== true) {
        showError('Hata', 'Kullanıcı adı değiştirilemedi');
        success = false;
      }
    }

    // Rol değiştiyse
    if (roleChanged && success) {
      const result = await apiCall<any>(async () => {
        const response = await fetch('https://n8n.mahir.ai/webhook/change_user_role', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userToEdit.user_id,
            role: editedRole,
            app_name: 'translate'
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Rol değiştirme yanıtı:', data);
        return data;
      }, {
        loadingKey: 'change-role',
        loadingMessage: 'Kullanıcı rolü değiştiriliyor...',
        errorMessage: 'Kullanıcı rolü değiştirilirken hata oluştu',
        showToast: false
      });

      if (!result || !Array.isArray(result) || result.length === 0 || result[0].success !== true) {
        showError('Hata', 'Kullanıcı rolü değiştirilemedi');
        success = false;
      }
    }

    if (success) {
      showSuccess('Başarılı', 'Kullanıcı bilgileri başarıyla güncellendi');
      setIsEditModalOpen(false);
      setUserToEdit(null);
      // Kullanıcı listesini yenile
      fetchUsers();
    }
  };

  // Silme modalını aç
  const handleOpenDeleteModal = (userId: number, username: string) => {
    setUserToDelete({ userId, username });
    setIsDeleteModalOpen(true);
  };

  // Şirkete kullanıcı atama onay modalını aç
  const handleOpenAssignConfirm = (companyId: number, companyName: string, userId: number, username: string) => {
    setAssignmentData({ companyId, companyName, userId, username });
    setIsAssignConfirmModalOpen(true);
  };

  // Dokümana kullanıcı atama onay modalını aç
  const handleOpenDocAssignConfirm = (documentId: string, documentName: string, userId: number, username: string) => {
    setDocAssignmentData({ documentId, documentName, userId, username });
    setIsAssignDocConfirmModalOpen(true);
  };

  // Şirkete kullanıcı ata
  const handleConfirmAssignToCompany = async () => {
    if (!assignmentData) return;

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/assign_to_company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: assignmentData.userId,
          company_id: assignmentData.companyId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Kullanıcı atama yanıtı:', data);
      return data;
    }, {
      loadingKey: 'assign-to-company',
      loadingMessage: 'Kullanıcı atanıyor...',
      errorMessage: 'Kullanıcı atanırken hata oluştu',
      showToast: false
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      showSuccess('Başarılı', 'Kullanıcı başarıyla şirkete atandı');
      setIsAssignConfirmModalOpen(false);
      setAssignmentData(null);
      // Şirket listesini yenile
      refreshCompanies();
    } else {
      showError('Hata', 'Kullanıcı atanamadı');
    }
  };

  // Dokümana kullanıcı ata
  const handleConfirmAssignToDocument = async () => {
    if (!docAssignmentData) return;

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/assign_to_document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: docAssignmentData.userId,
          document_id: docAssignmentData.documentId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Doküman kullanıcı atama yanıtı:', data);
      return data;
    }, {
      loadingKey: 'assign-to-document',
      loadingMessage: 'Kullanıcı atanıyor...',
      errorMessage: 'Kullanıcı atanırken hata oluştu',
      showToast: false
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      showSuccess('Başarılı', 'Kullanıcı başarıyla dokümana atandı');
      setIsAssignDocConfirmModalOpen(false);
      setDocAssignmentData(null);
      // Doküman listesini yenile
      if (selectedCompanyForDocs?.company_id) {
        fetchDocuments(selectedCompanyForDocs.company_id);
      }
    } else {
      showError('Hata', 'Kullanıcı atanamadı');
    }
  };

  // Kullanıcı sil - onaylandığında
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/delete_user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userToDelete.userId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Kullanıcı silme yanıtı:', data);
      return data;
    }, {
      loadingKey: 'delete-user',
      loadingMessage: 'Kullanıcı siliniyor...',
      errorMessage: 'Kullanıcı silinirken hata oluştu',
      showToast: false
    });

    // Response array formatında geliyor: [{ "success": true }]
    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      showSuccess('Başarılı', 'Kullanıcı başarıyla silindi');
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      // Kullanıcı listesini yenile
      fetchUsers();
    } else {
      showError('Hata', 'Kullanıcı silinemedi');
    }
  };

  // Şirketten kullanıcı yetkisi çıkartma onay modalını aç
  const handleOpenRemoveAssignConfirm = (companyId: number, companyName: string, userId: number, username: string) => {
    setRemoveAssignmentData({ companyId, companyName, userId, username });
    setIsRemoveAssignModalOpen(true);
  };

  // Dokümandan kullanıcı yetkisi çıkartma onay modalını aç
  const handleOpenRemoveDocAssignConfirm = (documentId: string, documentName: string, userId: number, username: string) => {
    setRemoveDocAssignmentData({ documentId, documentName, userId, username });
    setIsRemoveDocAssignModalOpen(true);
  };

  // Şirketten kullanıcı yetkisi çıkart
  const handleConfirmRemoveFromCompany = async () => {
    if (!removeAssignmentData) return;

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/remove_assign_to_company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: removeAssignmentData.userId,
          company_id: removeAssignmentData.companyId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Yetki çıkartma yanıtı:', data);
      return data;
    }, {
      loadingKey: 'remove-from-company',
      loadingMessage: 'Yetki çıkartılıyor...',
      errorMessage: 'Yetki çıkartılırken hata oluştu',
      showToast: false
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      showSuccess('Başarılı', `${removeAssignmentData.username} kullanıcısının ${removeAssignmentData.companyName} şirketi yetkisi başarıyla kaldırıldı`);
      setIsRemoveAssignModalOpen(false);
      setRemoveAssignmentData(null);
      // Şirket listesini yenile
      refreshCompanies();
    } else {
      showError('Hata', 'Yetki çıkartılamadı');
    }
  };

  // Dokümandan kullanıcı yetkisi çıkart
  const handleConfirmRemoveFromDocument = async () => {
    if (!removeDocAssignmentData) return;

    const result = await apiCall<any>(async () => {
      const response = await fetch('https://n8n.mahir.ai/webhook/remove_assign_to_document', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: removeDocAssignmentData.userId,
          document_id: removeDocAssignmentData.documentId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Doküman yetki çıkartma yanıtı:', data);
      return data;
    }, {
      loadingKey: 'remove-from-document',
      loadingMessage: 'Yetki çıkartılıyor...',
      errorMessage: 'Yetki çıkartılırken hata oluştu',
      showToast: false
    });

    if (result && Array.isArray(result) && result.length > 0 && result[0].success === true) {
      showSuccess('Başarılı', `${removeDocAssignmentData.username} kullanıcısının ${removeDocAssignmentData.documentName} dökümanı yetkisi başarıyla kaldırıldı`);
      setIsRemoveDocAssignModalOpen(false);
      setRemoveDocAssignmentData(null);
      // Doküman listesini yenile
      if (selectedCompanyForDocs?.company_id) {
        fetchDocuments(selectedCompanyForDocs.company_id);
      }
    } else {
      showError('Hata', 'Yetki çıkartılamadı');
    }
  };

  const checkOpenAIStatus = async () => {
    setIsCheckingOpenai(true);
    try {
      const response = await fetch('https://n8n.mahir.ai/webhook/check_openai_status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        let status: number;
        if (typeof data === 'number') {
          status = data;
        } else if (Array.isArray(data) && data.length > 0) {
          const first = data[0];
          status = typeof first === 'number' ? first : Number(Object.values(first)[0]);
        } else if (typeof data === 'object' && data !== null) {
          status = Number(Object.values(data)[0]);
        } else {
          status = Number(data);
        }
        setOpenaiStatus(status === 1 ? 1 : 0);
      } else {
        showError('Hata', 'OpenAI bakiye kontrolü başarısız oldu');
      }
    } catch {
      showError('Hata', 'OpenAI bakiye kontrolü sırasında hata oluştu');
    } finally {
      setIsCheckingOpenai(false);
    }
  };

  return (
    <div className="min-h-full p-3 sm:p-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        {/* Üst satır: geri — başlık — OpenAI */}
        <div className="flex items-center gap-2 sm:gap-3 mb-2">
          {/* Geri Butonu */}
          <button
            onClick={() => navigate('/translator')}
            className="p-2 sm:p-3 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg flex-shrink-0"
            title="Geri Dön"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
          </button>

          {/* Başlık — ortada */}
          <div className="flex-1 text-center min-w-0">
            <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white truncate">
              Çevirmen Yönetim Paneli
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 hidden sm:block mt-1">
              Çevirmen kullanıcılarını yönetin ve yetkilendirme ayarlarını yapın
            </p>
          </div>

          {/* OpenAI Bakiye Kontrol Butonu */}
          <button
            onClick={checkOpenAIStatus}
            disabled={isCheckingOpenai}
            className={`flex-shrink-0 group flex items-center gap-2 sm:gap-3 pl-2.5 sm:pl-3.5 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-full border-2 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 select-none disabled:cursor-not-allowed disabled:active:scale-100 ${
              openaiStatus === 1
                ? 'bg-green-50 dark:bg-green-950/40 border-green-400 dark:border-green-600 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-950/60'
                : openaiStatus === 0
                ? 'bg-red-50 dark:bg-red-950/40 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/60'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 hover:text-blue-700 dark:hover:text-blue-300'
            }`}
          >
            <div className="relative flex-shrink-0 w-3 h-3">
              {isCheckingOpenai ? (
                <svg className="w-3 h-3 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
              ) : openaiStatus === 1 ? (
                <div className="w-3 h-3 rounded-full bg-green-500" />
              ) : openaiStatus === 0 ? (
                <div className="w-3 h-3 rounded-full bg-red-500" />
              ) : (
                <>
                  <div className="absolute inset-0 rounded-full bg-gray-400 animate-ping opacity-50" />
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                </>
              )}
            </div>
            <div className="flex flex-col items-start leading-none gap-0.5 hidden sm:flex">
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-50">OpenAI Bakiye</span>
              <span className="text-sm font-semibold">
                {isCheckingOpenai ? 'Sorgulanıyor...' : openaiStatus === 1 ? 'Bakiye Mevcut' : openaiStatus === 0 ? 'Bakiye Tükendi' : 'Kontrol et'}
              </span>
            </div>
            {!isCheckingOpenai && (
              <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover:opacity-70 group-hover:rotate-180 transition-all duration-500 hidden sm:block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 sm:mb-8 overflow-x-auto">
        <div className="inline-flex rounded-2xl p-1 sm:p-1.5 bg-gray-100 dark:bg-gray-800 min-w-full sm:min-w-0 sm:w-auto">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-2 sm:py-3.5 font-semibold rounded-xl transition-all duration-300 flex-1 sm:flex-none justify-center whitespace-nowrap ${
              activeTab === 'users'
                ? 'bg-white dark:bg-gray-700 text-[#02c2ff] shadow-lg shadow-blue-500/20 scale-105'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 flex-shrink-0 ${activeTab === 'users' ? 'text-[#02c2ff]' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'users' ? 2.5 : 2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="text-xs sm:text-base">Kullanıcılar</span>
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            className={`flex items-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-2 sm:py-3.5 font-semibold rounded-xl transition-all duration-300 flex-1 sm:flex-none justify-center whitespace-nowrap ${
              activeTab === 'companies'
                ? 'bg-white dark:bg-gray-700 text-[#02c2ff] shadow-lg shadow-blue-500/20 scale-105'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 flex-shrink-0 ${activeTab === 'companies' ? 'text-[#02c2ff]' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'companies' ? 2.5 : 2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3" />
            </svg>
            <span className="text-xs sm:text-base">Şirketler</span>
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex items-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-2 sm:py-3.5 font-semibold rounded-xl transition-all duration-300 flex-1 sm:flex-none justify-center whitespace-nowrap ${
              activeTab === 'documents'
                ? 'bg-white dark:bg-gray-700 text-[#02c2ff] shadow-lg shadow-blue-500/20 scale-105'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50'
            }`}
          >
            <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 flex-shrink-0 ${activeTab === 'documents' ? 'text-[#02c2ff]' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'documents' ? 2.5 : 2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-xs sm:text-base">Dokümanlar</span>
          </button>
        </div>
      </div>

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <div className="rounded-2xl shadow-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        {/* Bölüm Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-white">
              Kullanıcı Yönetimi
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
              Toplam {users.length} kullanıcı
            </p>
          </div>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg transition-colors duration-200 text-sm sm:text-base"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Yeni Kullanıcı
          </button>
        </div>

        {/* Tablo */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-[#02c2ff] rounded-full animate-spin"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
              <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-lg font-medium">Henüz kullanıcı bulunmuyor</p>
              <p className="text-sm">Yeni kullanıcı eklemek için yukarıdaki butonu kullanın</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Kullanıcı Adı
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    E-posta
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Rol
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Oluşturma Tarihi
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr 
                    key={user.user_id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 font-medium">
                      #{user.user_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-[#02c2ff] to-[#0199cc] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-bold">
                            {user.username?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {user.username || 'Kullanıcı'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getRoleColor(user.app_role)}`}>
                        {getRoleLabel(user.app_role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(user.create_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEditModal(user)}
                          className="p-2 text-gray-600 hover:text-[#02c2ff] dark:text-gray-400 dark:hover:text-[#02c2ff] transition-colors duration-200"
                          title="Düzenle"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(user.user_id, user.username)}
                          className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-500 transition-colors duration-200"
                          title="Sil"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </div>
      )}

      {/* Companies Tab Content */}
      {activeTab === 'companies' && (
        <div className="rounded-2xl shadow-lg border p-3 sm:p-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
            Şirket Bazlı Yetkilendirme
          </h2>
          
          {isLoadingCompanies ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-[#02c2ff] rounded-full animate-spin"></div>
            </div>
          ) : companies.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
              <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3" />
              </svg>
              <p className="text-lg font-medium">Henüz şirket bulunmuyor</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {companies.map((company) => (
                <div
                  key={company.id}
                  className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-6 hover:shadow-lg transition-all duration-200"
                >
                  {/* Company Icon */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"></path>
                      </svg>
                    </div>
                  </div>

                  {/* Company Name */}
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 line-clamp-1">
                    {company.company_name || company.name}
                  </h3>

                  {/* Company Details */}
                  <div className="space-y-2 mb-4">
                    {/* Company Code */}
                    <div className="flex items-center text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300 w-20">
                        Kod:
                      </span>
                      <span className={`${company.company_code ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                        {company.company_code || '-'}
                      </span>
                    </div>

                    {/* Create Date */}
                    {company.company_create_date && (
                      <div className="flex items-center text-sm">
                        <span className="font-medium text-gray-700 dark:text-gray-300 w-20">
                          Tarih:
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {new Date(company.company_create_date).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Users Section */}
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                    {/* Created By */}
                    <div className="flex items-center text-sm">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">
                        Oluşturan:
                      </span>
                      <span className={`truncate ${company.created_by_username ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                        {company.created_by_username || '-'}
                      </span>
                    </div>

                    {/* Assigned User */}
                    <div className="flex items-center text-sm flex-wrap gap-1.5">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">
                          Atanan:
                        </span>
                      </div>
                      {company.assigned_usernames ? (
                        (() => {
                          const usernames = company.assigned_usernames.split(',').map(u => u.trim());
                          const firstUser = usernames[0];
                          const remainingCount = usernames.length - 1;
                          
                          return (
                            <>
                              {/* Tek kullanıcı varsa hover'lı, çok kullanıcı varsa hover'sız */}
                              {remainingCount === 0 ? (
                                <div className="relative group">
                                  <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded cursor-help">
                                    @{firstUser}
                                  </span>
                                  {/* Hover Tooltip - Tek kullanıcı */}
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[999999] shadow-lg">
                                    <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                      Atanan Kullanıcı
                                    </div>
                                    <div className="space-y-1.5">
                                      {(() => {
                                        const user = users.find(u => u.username === firstUser);
                                        return (
                                          <div className="flex items-center justify-between hover:bg-gray-800 dark:hover:bg-gray-200 px-2 py-1 rounded transition-colors">
                                            <span className="text-[10px]">@{firstUser}</span>
                                            {user && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOpenRemoveAssignConfirm(company.company_id || 0, company.company_name || company.name, user.user_id, user.username);
                                                }}
                                                className="text-red-400 hover:text-red-300 dark:text-red-500 dark:hover:text-red-600 transition-colors ml-2"
                                                title="Yetkiyi Kaldır"
                                              >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    {/* Arrow */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                                  @{firstUser}
                                </span>
                              )}
                              
                              {/* +N badge - sadece birden fazla kullanıcı varsa - TÜM kullanıcıları gösterir */}
                              {remainingCount > 0 && (
                                <div className="relative group">
                                  <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-100 dark:bg-purple-800/30 px-2 py-0.5 rounded cursor-help">
                                    +{remainingCount}
                                  </span>
                                  {/* Hover Tooltip - TÜM kullanıcılar */}
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[999999] shadow-lg">
                                    <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                      Tüm Atanan Kullanıcılar
                                    </div>
                                    <div className="space-y-1.5">
                                      {usernames.map((username, idx) => {
                                        const user = users.find(u => u.username === username);
                                        return (
                                          <div key={idx} className="flex items-center justify-between hover:bg-gray-800 dark:hover:bg-gray-200 px-2 py-1 rounded transition-colors">
                                            <span className="text-[10px]">@{username}</span>
                                            {user && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleOpenRemoveAssignConfirm(company.company_id || 0, company.company_name || company.name, user.user_id, user.username);
                                                }}
                                                className="text-red-400 hover:text-red-300 dark:text-red-500 dark:hover:text-red-600 transition-colors ml-2"
                                                title="Yetkiyi Kaldır"
                                              >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {/* Arrow */}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </div>
                  </div>

                  {/* User Assignment Dropdown */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Kullanıcı Ata:
                    </label>
                    <select
                      onChange={(e) => {
                        const userId = parseInt(e.target.value);
                        const selectedUser = users.find(u => u.user_id === userId);
                        if (userId && company.company_id && selectedUser) {
                          handleOpenAssignConfirm(
                            company.company_id, 
                            company.company_name || company.name, 
                            userId, 
                            selectedUser.username
                          );
                          e.target.value = ''; // Reset selection
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent outline-none transition-all cursor-pointer"
                    >
                      <option value="">Kullanıcı seçiniz...</option>
                      {users.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.username}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Documents Tab Content */}
      {activeTab === 'documents' && (
        <div className="rounded-2xl shadow-lg border p-3 sm:p-6 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <h2 className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
            Döküman Bazlı Yetkilendirme
          </h2>

          {/* Şirket Seçimi */}
          {!selectedCompanyForDocs ? (
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Dökümanları görüntülemek için bir şirket seçin:
              </p>
              
              {isLoadingCompanies ? (
                <div className="flex items-center justify-center p-12">
                  <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-[#02c2ff] rounded-full animate-spin"></div>
                </div>
              ) : companies.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
                  <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3" />
                  </svg>
                  <p className="text-lg font-medium">Henüz şirket bulunmuyor</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {companies.map((company) => (
                    <div
                      key={company.id}
                      className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer hover:border-[#02c2ff]"
                      onClick={() => {
                        setSelectedCompanyForDocs(company);
                        fetchDocuments(company.company_id || 0);
                      }}
                    >
                      {/* Company Icon */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h5m-5 0v-6a2 2 0 012-2h1a2 2 0 012 2v6m-3 0h3"></path>
                          </svg>
                        </div>
                      </div>

                      {/* Company Name */}
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 line-clamp-1">
                        {company.company_name || company.name}
                      </h3>

                      {/* Company Details */}
                      <div className="space-y-2 mb-4">
                        {/* Company Code */}
                        <div className="flex items-center text-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-300 w-20">
                            Kod:
                          </span>
                          <span className={`${company.company_code ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                            {company.company_code || '-'}
                          </span>
                        </div>

                        {/* Create Date */}
                        {company.company_create_date && (
                          <div className="flex items-center text-sm">
                            <span className="font-medium text-gray-700 dark:text-gray-300 w-20">
                              Tarih:
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {new Date(company.company_create_date).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Users Section */}
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                        {/* Created By */}
                        <div className="flex items-center text-sm">
                          <svg className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">
                            Oluşturan:
                          </span>
                          <span className={`truncate ${company.created_by_username ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                            {company.created_by_username || '-'}
                          </span>
                        </div>

                        {/* Assigned User */}
                        <div className="flex items-center text-sm flex-wrap gap-1.5">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">
                              Atanan:
                            </span>
                          </div>
                          {company.assigned_usernames ? (
                            (() => {
                              const usernames = company.assigned_usernames.split(',').map(u => u.trim());
                              const firstUser = usernames[0];
                              const remainingCount = usernames.length - 1;
                              
                              return (
                                <>
                                  {/* Tek kullanıcı varsa hover'lı, çok kullanıcı varsa hover'sız */}
                                  {remainingCount === 0 ? (
                                    <div className="relative group">
                                      <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded cursor-help">
                                        @{firstUser}
                                      </span>
                                      {/* Hover Tooltip - Tek kullanıcı */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                        <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                          Atanan Kullanıcı
                                        </div>
                                        <div className="text-[10px] text-center">
                                          @{firstUser}
                                        </div>
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                                      @{firstUser}
                                    </span>
                                  )}
                                  
                                  {/* +N badge - sadece birden fazla kullanıcı varsa - TÜM kullanıcıları gösterir */}
                                  {remainingCount > 0 && (
                                    <div className="relative group">
                                      <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-100 dark:bg-purple-800/30 px-2 py-0.5 rounded cursor-help">
                                        +{remainingCount}
                                      </span>
                                      {/* Hover Tooltip - TÜM kullanıcılar */}
                                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[999999] shadow-lg">
                                        <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                          Tüm Atanan Kullanıcılar
                                        </div>
                                        <div className="space-y-1">
                                          {usernames.map((username, idx) => (
                                            <div key={idx} className="text-[10px]">
                                              @{username}
                                            </div>
                                          ))}
                                        </div>
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Döküman Listesi */
            <div>
              {/* Back Button */}
              <button
                onClick={() => {
                  setSelectedCompanyForDocs(null);
                  setDocuments([]);
                }}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-[#02c2ff] dark:hover:text-[#02c2ff] mb-6 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Şirket Seçimine Dön
              </button>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {selectedCompanyForDocs.company_name || selectedCompanyForDocs.name} - Dökümanlar
              </h3>

              {isLoadingDocuments ? (
                <div className="flex items-center justify-center p-12">
                  <div className="w-8 h-8 border-4 border-gray-300 dark:border-gray-600 border-t-[#02c2ff] rounded-full animate-spin"></div>
                </div>
              ) : documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-gray-500 dark:text-gray-400">
                  <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium">Bu şirkete ait döküman bulunmuyor</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {documents.map((doc) => {
                    const checkedCount = parseInt(String(doc.checked_count || 0));
                    const uncheckedCount = parseInt(String(doc.unchecked_count || 0));
                    const totalCount = checkedCount + uncheckedCount;
                    
                    // SVG circle için circumference hesaplama (2 * π * r)
                    const radius = 20;
                    const circumference = 2 * Math.PI * radius;
                    
                    // Yüzdeleri circumference'a göre hesapla
                    const checkedLength = totalCount > 0 ? (checkedCount / totalCount) * circumference : 0;
                    const uncheckedLength = totalCount > 0 ? (uncheckedCount / totalCount) * circumference : 0;
                    
                    // Format date and time
                    const formatDateTime = (dateStr: string, timeStr: string) => {
                      if (!dateStr || !timeStr) return '';
                      const year = dateStr.substring(0, 4);
                      const month = dateStr.substring(4, 6);
                      const day = dateStr.substring(6, 8);
                      const hour = timeStr.substring(0, 2);
                      const minute = timeStr.substring(2, 4);
                      return `${day}.${month}.${year} ${hour}:${minute}`;
                    };

                    return (
                      <div
                        key={doc.document_id}
                        className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-6 hover:shadow-lg transition-all duration-200"
                      >
                        {/* Header with Icon and Pie Chart */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          
                          {/* Pie Chart */}
                          {totalCount > 0 && (
                            <div className="relative group">
                              <div className="relative">
                                <svg width="56" height="56" viewBox="0 0 56 56" className="transform -rotate-90">
                                  {/* Outer border (frame) */}
                                  <circle
                                    cx="28"
                                    cy="28"
                                    r="24"
                                    fill="none"
                                    stroke="#d1d5db"
                                    className="dark:stroke-gray-600"
                                    strokeWidth="2"
                                  />
                                  {/* Background circle (empty part) */}
                                  <circle
                                    cx="28"
                                    cy="28"
                                    r={radius}
                                    fill="none"
                                    stroke="#f3f4f6"
                                    className="dark:stroke-gray-700"
                                    strokeWidth="8"
                                  />
                                  {/* Checked segment (green fill) */}
                                  <circle
                                    cx="28"
                                    cy="28"
                                    r={radius}
                                    fill="none"
                                    stroke="#10b981"
                                    strokeWidth="8"
                                    strokeDasharray={`${checkedLength} ${circumference - checkedLength}`}
                                    strokeDashoffset="0"
                                    strokeLinecap="round"
                                    className="transition-all duration-500"
                                  />
                                </svg>
                                
                                {/* Percentage Text in Center */}
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className={`text-xs font-bold ${
'text-gray-700 dark:text-gray-200'
                                  }`}>
                                    {Math.round((checkedCount / totalCount) * 100)}%
                                  </span>
                                </div>
                              </div>
                              
                              {/* Tooltip on hover */}
                              <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                <div className={`${
'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                                } text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                    <span>Kontrol Edildi: {checkedCount}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                                    <span>Kontrol Edilmedi: {uncheckedCount}</span>
                                  </div>
                                  <div className="pt-1 border-t border-gray-600 dark:border-gray-400 mt-1">
                                    <span className="font-semibold">Toplam: {checkedCount + uncheckedCount}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Document File Name - Main Title */}
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2">
                          {doc.file_name || doc.file_name_no_ext}
                        </h3>

                        {/* Document Details */}
                        <div className="space-y-2 mb-4">
                          {/* Document ID */}
                          <div className="flex items-center text-xs">
                            <span className="font-medium text-gray-700 dark:text-gray-300 w-16">
                              ID:
                            </span>
                            <span className="text-gray-600 dark:text-gray-400 truncate">
                              {doc.document_id}
                            </span>
                          </div>

                          {/* Date & Time */}
                          {doc.current_date && doc.current_time && (
                            <div className="flex items-center text-xs">
                              <span className="font-medium text-gray-700 dark:text-gray-300 w-16">
                                Tarih:
                              </span>
                              <span className="text-gray-600 dark:text-gray-400">
                                {formatDateTime(doc.current_date, doc.current_time)}
                              </span>
                            </div>
                          )}

                          {/* Stage (Durum) */}
                          {doc.stage !== undefined && (
                            <div className="flex items-center text-xs pt-2">
                              <span className="font-medium text-gray-700 dark:text-gray-300 w-16">
                                Durum:
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                doc.stage === 0 
                                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                  : doc.stage === 1
                                  ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                                  : 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                              }`}>
                                {doc.stage === 0 
                                  ? 'Etiketlendi'
                                  : doc.stage === 1
                                  ? 'İlk Taslak'
                                  : `Revize ${(doc.stage ?? 0) - 1}`
                                }
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Users Section */}
                        <div className="pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                          {/* Translator */}
                          <div className="flex items-center text-sm">
                            <svg className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">
                              Çeviren:
                            </span>
                            <span className={`truncate ${doc.username ? 'text-gray-600 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                              {doc.username || '-'}
                            </span>
                          </div>

                          {/* Assigned User */}
                          <div className="flex items-center text-sm flex-wrap gap-1.5">
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                              </svg>
                              <span className="font-medium text-gray-700 dark:text-gray-300 mr-2">
                                Atanan:
                              </span>
                            </div>
                            {doc.assigned_usernames ? (
                              (() => {
                                const usernames = doc.assigned_usernames.split(',').map(u => u.trim());
                                const firstUser = usernames[0];
                                const remainingCount = usernames.length - 1;
                                
                                return (
                                  <>
                                    {/* Tek kullanıcı varsa hover'lı, çok kullanıcı varsa hover'sız */}
                                    {remainingCount === 0 ? (
                                      <div className="relative group">
                                        <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded cursor-help">
                                          @{firstUser}
                                        </span>
                                        {/* Hover Tooltip - Tek kullanıcı */}
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[999999] shadow-lg">
                                          <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                            Atanan Kullanıcı
                                          </div>
                                          <div className="space-y-1.5">
                                            {(() => {
                                              const user = users.find(u => u.username === firstUser);
                                              return (
                                                <div className="flex items-center justify-between hover:bg-gray-800 dark:hover:bg-gray-200 px-2 py-1 rounded transition-colors">
                                                  <span className="text-[10px]">@{firstUser}</span>
                                                  {user && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenRemoveDocAssignConfirm(doc.document_id, doc.file_name_no_ext, user.user_id, user.username);
                                                      }}
                                                      className="text-red-400 hover:text-red-300 dark:text-red-500 dark:hover:text-red-600 transition-colors ml-2"
                                                      title="Yetkiyi Kaldır"
                                                    >
                                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                      </svg>
                                                    </button>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                          </div>
                                          {/* Arrow */}
                                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-50 dark:bg-purple-900/20 px-2 py-0.5 rounded">
                                        @{firstUser}
                                      </span>
                                    )}
                                    
                                    {/* +N badge - sadece birden fazla kullanıcı varsa - TÜM kullanıcıları gösterir */}
                                    {remainingCount > 0 && (
                                      <div className="relative group">
                                        <span className="text-sm text-gray-700 dark:text-gray-300 bg-purple-100 dark:bg-purple-800/30 px-2 py-0.5 rounded cursor-help">
                                          +{remainingCount}
                                        </span>
                                        {/* Hover Tooltip - TÜM kullanıcılar */}
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto z-[999999] shadow-lg">
                                          <div className="font-semibold mb-2 text-center border-b border-gray-700 dark:border-gray-300 pb-1">
                                            Tüm Atanan Kullanıcılar
                                          </div>
                                          <div className="space-y-1.5">
                                            {usernames.map((username, idx) => {
                                              const user = users.find(u => u.username === username);
                                              return (
                                                <div key={idx} className="flex items-center justify-between hover:bg-gray-800 dark:hover:bg-gray-200 px-2 py-1 rounded transition-colors">
                                                  <span className="text-[10px]">@{username}</span>
                                                  {user && (
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenRemoveDocAssignConfirm(doc.document_id, doc.file_name_no_ext, user.user_id, user.username);
                                                      }}
                                                      className="text-red-400 hover:text-red-300 dark:text-red-500 dark:hover:text-red-600 transition-colors ml-2"
                                                      title="Yetkiyi Kaldır"
                                                    >
                                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                      </svg>
                                                    </button>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                          {/* Arrow */}
                                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">-</span>
                            )}
                          </div>
                        </div>

                        {/* User Assignment Dropdown */}
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Kullanıcı Ata:
                          </label>
                          <select
                            onChange={(e) => {
                              const userId = parseInt(e.target.value);
                              const selectedUser = users.find(u => u.user_id === userId);
                              if (userId && doc.document_id && selectedUser) {
                                handleOpenDocAssignConfirm(
                                  doc.document_id,
                                  doc.file_name_no_ext,
                                  userId,
                                  selectedUser.username
                                );
                                e.target.value = ''; // Reset selection
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-600 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent outline-none transition-all cursor-pointer"
                          >
                            <option value="">Kullanıcı seçiniz...</option>
                            {users.map((user) => (
                              <option key={user.user_id} value={user.user_id}>
                                {user.username}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Şirkete Kullanıcı Atama Onay Modal */}
      {isAssignConfirmModalOpen && assignmentData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[450px] max-w-[90vw] shadow-2xl">
            {/* Modal Icon & Title */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-center">Kullanıcı Atamasını Onayla</h3>
            </div>

            {/* Message */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
              <p className="text-center text-base mb-3">
                <span className="font-semibold text-[#02c2ff]">{assignmentData.username}</span> kullanıcısını
              </p>
              <p className="text-center text-base">
                <span className="font-semibold text-[#02c2ff]">{assignmentData.companyName}</span> şirketine atamak istediğinizden emin misiniz?
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsAssignConfirmModalOpen(false);
                  setAssignmentData(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmAssignToCompany}
                className="flex-1 px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium transition-colors"
              >
                Onayla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dokümana Kullanıcı Atama Onay Modal */}
      {isAssignDocConfirmModalOpen && docAssignmentData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[450px] max-w-[90vw] shadow-2xl">
            {/* Modal Icon & Title */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-center">Kullanıcı Atamasını Onayla</h3>
            </div>

            {/* Message */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
              <p className="text-center text-base mb-3">
                <span className="font-semibold text-[#02c2ff]">{docAssignmentData.username}</span> kullanıcısını
              </p>
              <p className="text-center text-base">
                <span className="font-semibold text-[#02c2ff]">{docAssignmentData.documentName}</span> dökümanına atamak istediğinizden emin misiniz?
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsAssignDocConfirmModalOpen(false);
                  setDocAssignmentData(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmAssignToDocument}
                className="flex-1 px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium transition-colors"
              >
                Onayla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Düzenleme Modal */}
      {isEditModalOpen && userToEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold">Kullanıcıyı Düzenle</h3>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setUserToEdit(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Form */}
            <div className="space-y-5">
              {/* Kullanıcı ID (Read-only) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Kullanıcı ID
                </label>
                <input
                  type="text"
                  value={`#${userToEdit.user_id}`}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Kullanıcı Adı */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Kullanıcı Adı
                </label>
                <input
                  type="text"
                  value={editedUsername}
                  onChange={(e) => setEditedUsername(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent outline-none transition-all"
                  placeholder="Kullanıcı adını giriniz"
                />
              </div>

              {/* E-posta (Read-only) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  E-posta
                </label>
                <input
                  type="email"
                  value={userToEdit.email}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              {/* Rol */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Rol
                </label>
                <select
                  value={editedRole}
                  onChange={(e) => setEditedRole(e.target.value as 'admin' | 'editor' | 'redactor')}
                  className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent outline-none transition-all"
                >
                  <option value="admin">Yönetici</option>
                  <option value="editor">Editör</option>
                  <option value="redactor">Redaktör</option>
                </select>
              </div>

              {/* Oluşturma Tarihi (Read-only) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Oluşturma Tarihi
                </label>
                <input
                  type="text"
                  value={formatDate(userToEdit.create_date)}
                  disabled
                  className="w-full px-4 py-3 rounded-lg border bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setUserToEdit(null);
                }}
                className="px-6 py-3 rounded-lg font-medium transition-colors bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                İptal
              </button>
              <button
                onClick={handleEditUser}
                className="px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium transition-colors"
              >
                Değişiklikleri Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Oluşturma Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-semibold">
                {createStep === 'email' && 'Yeni Kullanıcı Ekle'}
                {createStep === 'create' && 'Kullanıcı Bilgileri'}
                {createStep === 'assign-role' && 'Rol Ataması'}
                {createStep === 'success' && 'Kullanıcı Yaratıldı'}
              </h3>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Step 1: Email girişi */}
            {createStep === 'email' && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    E-posta Adresi <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent outline-none transition-all"
                    placeholder="ornek@email.com"
                    onKeyPress={(e) => e.key === 'Enter' && handleCheckEmail()}
                  />
                </div>
                
                <div className="flex justify-end gap-3 mt-8">
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-6 py-3 rounded-lg font-medium transition-colors bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleCheckEmail}
                    className="px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium transition-colors"
                  >
                    Devam Et
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Yeni kullanıcı yaratma formu */}
            {createStep === 'create' && (
              <div className="space-y-5">
                {/* Username (readonly) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Kullanıcı Adı
                  </label>
                  <input
                    type="text"
                    value={newUserEmail}
                    readOnly
                    className="w-full px-4 py-3 rounded-lg border bg-gray-100 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 outline-none cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Kullanıcı yaratıldıktan sonra değiştirilebilir
                  </p>
                </div>

                {/* Email (readonly) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    E-posta
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    readOnly
                    className="w-full px-4 py-3 rounded-lg border bg-gray-100 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 outline-none cursor-not-allowed"
                  />
                </div>

                {/* Rol */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Rol <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'editor' | 'redactor')}
                    className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent outline-none transition-all"
                  >
                    <option value="admin">Yönetici</option>
                    <option value="editor">Editör</option>
                    <option value="redactor">Redaktör</option>
                  </select>
                </div>

                {/* Password (readonly) */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Şifre
                  </label>
                  <input
                    type="text"
                    value={generatedPassword}
                    readOnly
                    className="w-full px-4 py-3 rounded-lg border bg-gray-100 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 outline-none cursor-not-allowed font-mono"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Kullanıcı yaratıldıktan sonra değiştirilebilir
                  </p>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-6 py-3 rounded-lg font-medium transition-colors bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleCreateUser}
                    className="px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium transition-colors"
                  >
                    Kullanıcı Yarat
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Rol ataması (mevcut kullanıcı için) */}
            {createStep === 'assign-role' && (
              <div className="space-y-5">
                {/* Info message */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Kullanıcı Zaten Kayıtlı
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        Bu kullanıcı {getAppDisplayName(existAppName || '')} uygulamasında kayıtlı. Sadece Çevirmen uygulaması için rol ataması yapılacak.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Rol seçimi */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Rol <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'editor' | 'redactor')}
                    className="w-full px-4 py-3 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#02c2ff] focus:border-transparent outline-none transition-all"
                  >
                    <option value="admin">Yönetici</option>
                    <option value="editor">Editör</option>
                    <option value="redactor">Redaktör</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-6 py-3 rounded-lg font-medium transition-colors bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleAssignRole}
                    className="px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium transition-colors"
                  >
                    Rol Ata
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Success ekranı */}
            {createStep === 'success' && (
              <div className="space-y-5">
                {/* Success icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                </div>

                <p className="text-center text-gray-700 dark:text-gray-300 mb-6">
                  Kullanıcı başarıyla yaratıldı. Aşağıdaki bilgileri kullanıcıya iletin.
                </p>

                {/* Kullanıcı Bilgileri - Tek Blok */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 border-2 border-gray-200 dark:border-gray-700">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">E-posta:</p>
                      <p className="text-lg font-medium text-gray-900 dark:text-white">{createdUserEmail}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">Şifre:</p>
                      <p className="text-lg font-mono font-medium text-gray-900 dark:text-white">{createdUserPassword}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      const credentials = `E-posta: ${createdUserEmail}\nŞifre: ${createdUserPassword}`;
                      copyToClipboard(credentials, 'Kullanıcı bilgileri');
                    }}
                    className="w-full mt-6 px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                    Bilgileri Kopyala
                  </button>
                </div>

                <div className="flex justify-end mt-8">
                  <button
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-6 py-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg font-medium transition-colors"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Şirketten Yetki Çıkartma Onay Modal */}
      {isRemoveAssignModalOpen && removeAssignmentData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Warning Icon and Title */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Şirket Yetkisini Kaldır
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Kullanıcının şirket erişimi kaldırılacak
                </p>
              </div>
            </div>

            {/* Message */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Kullanıcı:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">@{removeAssignmentData.username}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Şirket:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{removeAssignmentData.companyName}</span>
                </div>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-8">
              Bu kullanıcının şirkete erişimi kaldırılacak. Kullanıcı bu şirketin dökümanlarını göremeyecek ve işlem yapamayacak. Emin misiniz?
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsRemoveAssignModalOpen(false);
                  setRemoveAssignmentData(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmRemoveFromCompany}
                className="flex-1 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6"/>
                </svg>
                Evet, Yetkiyi Kaldır
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dokümandan Yetki Çıkartma Onay Modal */}
      {isRemoveDocAssignModalOpen && removeDocAssignmentData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Warning Icon and Title */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Döküman Yetkisini Kaldır
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Kullanıcının döküman erişimi kaldırılacak
                </p>
              </div>
            </div>

            {/* Message */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Kullanıcı:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">@{removeDocAssignmentData.username}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Döküman:</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white truncate ml-2">{removeDocAssignmentData.documentName}</span>
                </div>
              </div>
            </div>

            <p className="text-gray-700 dark:text-gray-300 mb-8">
              Bu kullanıcının dökümana erişimi kaldırılacak. Kullanıcı bu dökümanı göremeyecek ve işlem yapamayacak. Emin misiniz?
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsRemoveDocAssignModalOpen(false);
                  setRemoveDocAssignmentData(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmRemoveFromDocument}
                className="flex-1 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                Evet, Yetkiyi Kaldır
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Silme Onay Modal */}
      {isDeleteModalOpen && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-8 w-[500px] max-w-[90vw] shadow-2xl">
            {/* Warning Icon and Title */}
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Kullanıcıyı Sil
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Bu işlem geri alınamaz
                </p>
              </div>
            </div>

            {/* Message */}
            <div className="mb-8 space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">"{userToDelete.username}"</span> kullanıcısını silmek istediğinizden emin misiniz?
              </p>
              
              {/* Dikkat Çekici Uyarı */}
              <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                  </svg>
                  <div className="flex-1">
                    <p className="font-bold text-red-900 dark:text-red-100 mb-1">
                      ÖNEMLİ UYARI
                    </p>
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                      Bu kullanıcı <span className="font-bold underline">TÜM UYGULAMALARDAN</span> (Çevirmen ve İçerik Güncelleme) kalıcı olarak silinecektir. Bu işlem geri alınamaz!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setUserToDelete(null);
                }}
                className="flex-1 px-6 py-3 rounded-lg font-medium transition-colors border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                İptal
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Çeviri Ayarları Modalı */}
      {showTranslationSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Çeviri Ayarları
              </h3>
              <button
                onClick={() => setShowTranslationSettings(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-6">
              {/* Kalite Kontrol Modeli */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Kalite Kontrol Modeli
                </label>
                
                {/* Loading state for current model */}
                {isLoadingCurrentModel && (
                  <div className="flex items-center justify-center p-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Mevcut model bilgisi yükleniyor...</span>
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  {/* GPT-5.2 */}
                  <label className="flex items-start space-x-3 p-3 border-2 border-purple-500 dark:border-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer transition-colors relative">
                    <input
                      type="radio"
                      name="qualityModel"
                      value="gpt-5.2"
                      checked={selectedQualityModel === 'gpt-5.2'}
                      onChange={(e) => handleModelSelection(e.target.value as 'gpt-5.2')}
                      disabled={isSubmittingModel || isLoadingCurrentModel}
                      className="mt-1 text-purple-600 focus:ring-purple-500 disabled:opacity-50"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-gray-900 dark:text-white">GPT-5.2</div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
                          YENİ
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        En gelişmiş ve en güçlü model. Üstün kalite ve hassasiyet.
                      </div>
                    </div>
                  </label>

                  {/* GPT-5 */}
                  <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="qualityModel"
                      value="gpt-5"
                      checked={selectedQualityModel === 'gpt-5'}
                      onChange={(e) => handleModelSelection(e.target.value as 'gpt-5')}
                      disabled={isSubmittingModel || isLoadingCurrentModel}
                      className="mt-1 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">GPT-5</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Güçlü ve doğru sonuçlar veren model.
                      </div>
                    </div>
                  </label>

                  {/* GPT-5 Mini */}
                  <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="qualityModel"
                      value="gpt-5-mini"
                      checked={selectedQualityModel === 'gpt-5-mini'}
                      onChange={(e) => handleModelSelection(e.target.value as 'gpt-5-mini')}
                      disabled={isSubmittingModel || isLoadingCurrentModel}
                      className="mt-1 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">GPT-5 Mini</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Daha düşük maliyetle güçlü sonuçlar veren model.
                      </div>
                    </div>
                  </label>

                  {/* GPT-4o Mini */}
                  <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="qualityModel"
                      value="gpt-4o-mini"
                      checked={selectedQualityModel === 'gpt-4o-mini'}
                      onChange={(e) => handleModelSelection(e.target.value as 'gpt-4o-mini')}
                      disabled={isSubmittingModel || isLoadingCurrentModel}
                      className="mt-1 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">GPT-4o Mini</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        En ekonomik ve temel işler için uygun model.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Loading Indicator */}
            {isSubmittingModel && (
              <div className="flex items-center justify-center mt-4 p-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Model güncelleniyor...</span>
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex justify-end mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => setShowTranslationSettings(false)}
                disabled={isSubmittingModel || isLoadingCurrentModel}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;

